import { ActivatedRoute, Router } from '@angular/router';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewContainerRef,
  ChangeDetectionStrategy,
  forwardRef,
} from '@angular/core';
import {
  EditorFormEntity,
  FormConfiguration,
  FormMode,
  FormSaveResult,
} from 'src/app/core/models/form.models';
import {
  FormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Role, RoleComponent, RoleModule } from '../../admin.models';
import { injectSaveRoleMutation } from 'src/app/api/queries/role.queries';

import { AppState } from 'src/app/core/state/app.state';
import { AuditTrailComponent } from 'src/app/shared/components/audit-trail/audit-trail.component';
import { BreadcrumbService } from 'src/app/core/services/breadcrumb.service';
import { FormsService } from 'src/app/core/services/forms.service';
import { GeneralFormEditorComponent } from 'src/app/shared/components/general-form-editor/general-form-editor.component';
import { LoggerService } from '@services/logger.service';
import { NomenclatureService } from 'src/app/core/services/nomenclature.service';
import { PopupMetadata } from 'src/app/core/constants';
import { Principal } from 'src/app/core/models/object.models';
import { RoleFeaturesComponent } from './role-features/role-features.component';
import { RoleApiService } from 'src/app/api/services/role-api.service';
import { Store } from '@ngrx/store';
import { ToastService } from '@services/toast.service';
import { UserGroupMembersComponent } from '../../components/user-group-members/user-group-members.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, map, of, tap } from 'rxjs';
import { NgClass, NgStyle, NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { DynamicFormComponent } from '../../../shared/components/dynamic-form/dynamic-form.component';
import { CdkPortal } from '@angular/cdk/portal';
import { SearchInputComponent } from '../../../shared/components/search-input/search-input.component';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { LoadingComponent } from '../../../shared/components/loading/loading.component';
import { SuiButtonComponent } from '@sui/button';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-editor-role',
  templateUrl:
    '../../../shared/components/general-form-editor/general-form-editor.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgClass,
    NgStyle,
    forwardRef(() => DynamicFormComponent),
    FormsModule,
    ReactiveFormsModule,
    NgTemplateOutlet,
    CdkPortal,
    SearchInputComponent,
    FaIconComponent,
    LoadingComponent,
    SuiButtonComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class EditorRoleComponent
  extends GeneralFormEditorComponent
  implements AfterViewInit
{
  @Input() role: Role | null;
  @Input() isPopup: boolean;
  @Input() popupMetadata: PopupMetadata | null;

  @Output() cancelled = new EventEmitter<void>();

  roleModules: RoleModule[] = [];
  editorFormEntityName = EditorFormEntity.Role;

  private readonly saveRoleMutation = injectSaveRoleMutation();

  constructor(
    protected formBuilder: FormBuilder,
    protected formService: FormsService,
    protected nomService: NomenclatureService,
    protected toastService: ToastService,
    protected router: Router,
    protected activatedRoute: ActivatedRoute,
    protected store: Store<AppState>,
    protected roleApi: RoleApiService,
    protected logger: LoggerService,
    protected breadcrumbService: BreadcrumbService,
    protected viewContainerRef: ViewContainerRef,
  ) {
    super(
      formBuilder,
      formService,
      toastService,
      store,
      logger,
      router,
      activatedRoute,
      breadcrumbService,
      viewContainerRef,
    );

    // * Initially handle standalone mode (route-based) role
    // * @Input() role is not available yet in constructor, will be handled in ngOnInit
    this.initializeRole(this.activatedRoute.snapshot.data['role']);
  }

  ngOnInit(): void {
    // ! Set formMode BEFORE parent initialization!!!
    const hasRole = Boolean(this.role?.id);
    this.formMode = hasRole ? FormMode.Edit : FormMode.Create;

    super.ngOnInit();

    this.isPopupEditor = this.isPopup;

    // * handle popup mode: @Input() role takes precedence over route data
    if (this.isPopup) {
      this.initializeRole(this.role);
    }

    // * only populate form if we have an existing Role (Edit mode)
    if (this.role && this.role.id) {
      this.formService.populateFormValues(this.formGroup, this.role);
      this.validateAndHandleInvalidForm();
    }

    // * Always set up validation sync - but it only shows errors when validationEnabled is true
    this.setupValidationSync();
  }

  private initializeRole(inputRole: Role | null): void {
    const isCreateMode = inputRole === null || inputRole === undefined;

    if (isCreateMode) {
      this.role = {
        id: null,
        title: '',
        description: '',
        object_class_id: 0,
        object_type_id: 0,
        components: [],
        members: [],
        lifecycle_status: 0,
      };
    } else {
      // * Edit mode
      this.role = { ...inputRole };
    }

    // * Fetch role modules and set up component selection
    this.setupRoleModulesAndComponents();
  }

  private setupRoleModulesAndComponents(): void {
    // * Fetch role modules and components
    this.roleApi
      .getModuleComponents()
      .pipe(
        tap((modules) => {
          this.roleModules = modules;
          let components = [];
          // * Set the selected flag for each Component in the Role
          this.roleModules.forEach((module) => {
            // * check Root component first
            if (module.root_component) {
              const roleComponent = this.role.components.find(
                (c) => c.id === module.root_component.id,
              );
              module.root_component.selected = roleComponent ? true : false;
              if (module.root_component.selected) {
                components.push(module.root_component);
              }
            }

            // * check Child components
            module.components.forEach((component) => {
              // * in Create mode, this.role.components is empty array, so no components are selected
              // * in Edit mode, find existing selected components
              const roleComponent = this.role.components.find(
                (c) => c.id === component.id,
              );
              component.selected = roleComponent ? true : false;
              if (component.selected) {
                components.push(component);
              }
            });
          });

          this.role = {
            ...this.role,
            components: components,
          };
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onCancel(): void {
    this.cancelled.emit();

    // ! only navigate if we're not in Popup Mode!
    if (!this.isPopup) {
      this.router.navigate(['admin', 'roles']);
    }
  }

  override submit(): Promise<unknown> | void {
    return super.submit();
  }

  protected override saveViaMutation(formData: any): Promise<FormSaveResult> {
    const role: Role = {
      ...this.role,
      ...formData,
      // ! Ensure description is always a string, never null
      description: formData.description || '',
    };
    const isCreated = !role.id;

    return this.saveRoleMutation
      .mutateAsync(role)
      .then((saved) => ({ object: saved, isCreated }));
  }

  onObjectCreated(object: any): void {}

  onObjectUpdated(object: any): void {}

  /**
   * Returns an Observable that emits role modules data
   * If data already loaded by setupRoleModulesAndComponents(), returns it immediately
   * Otherwise, fetches fresh data (shouldn't happen in normal flow)
   * Used by inputProviderAsync for proper async initialization with eager loading
   */
  getRoleModulesAsync(): Observable<any> {
    // * Data already loaded with selection state? Return it immediately
    if (this.roleModules && this.roleModules.length > 0) {
      return of({
        roleModules: this.roleModules,
        searchString$: this.searchString$,
      });
    }

    // * Fallback: Data not loaded yet, fetch it (shouldn't happen normally)
    return this.roleApi.getModuleComponents().pipe(
      map((modules) => {
        // * Apply selection logic (same as setupRoleModulesAndComponents)
        modules.forEach((module) => {
          if (module.root_component) {
            const roleComponent = this.role?.components?.find(
              (c) => c.id === module.root_component.id,
            );
            module.root_component.selected = roleComponent ? true : false;
          }

          module.components?.forEach((component) => {
            const roleComponent = this.role?.components?.find(
              (c) => c.id === component.id,
            );
            component.selected = roleComponent ? true : false;
          });
        });

        return {
          roleModules: modules,
          searchString$: this.searchString$,
        };
      }),
    );
  }

  getMembersComponentInputs(): any {
    return {
      principals: this.role?.members ?? [],
    };
  }

  // Override the abstract method from the parent to provide form configuration
  loadFormConfig(params?: any): FormConfiguration {
    return {
      tabs: [
        // ! General Settings Tab
        {
          label: this.translateService.instant(
            'editor-role.label.general-settings',
          ),
          key: 'general-settings',
          icon: ['fas', 'cog'],
          panels: [
            {
              panelName: 'Role Settings Panel',
              panelLabel: '',
              layoutConfig: {
                direction: 'column',
                gap: '0.75rem',
              },
              items: [
                {
                  propertyName: 'title',
                  propertyLabel: this.translateService.instant(
                    'editor-role.label.role-name',
                  ),
                  controlType: 'text',
                  validators: [Validators.required],
                },
                {
                  propertyName: 'description',
                  propertyLabel: this.translateService.instant(
                    'editor-role.label.description',
                  ),
                  controlType: 'textarea',
                  additionalConfig: {
                    placeholder: 'Enter role description...',
                    rows: 4,
                  },
                },
              ],
            },
          ],
          tabIndex: 0,
          name: '',
        },
        // ! Permissions Tab, assuming roles have permissions that can be edited
        {
          label: this.translateService.instant('editor-role.label.permissions'),
          key: 'permissions',
          icon: ['fas', 'shield-alt'],
          formPanelsClass: 'no-y-scroll', // added and applied on the container div with all panels
          panels: [
            {
              panelName: 'Permissions Panel',
              panelLabel: '',
              layoutConfig: {
                direction: 'column',
                gap: '0.75rem',
              },
              items: [
                {
                  propertyName: 'permissions',
                  propertyLabel: this.translateService.instant(
                    'editor-role.label.permissions',
                  ),
                  controlType: 'component',
                  additionalConfig: {
                    component: RoleFeaturesComponent,
                    inputProviderAsync: this.getRoleModulesAsync.bind(this),
                    outputs: {
                      selectedComponentsChange: (
                        selectedComponents: RoleComponent[],
                      ) => {
                        this.role = {
                          ...this.role,
                          components: selectedComponents,
                        };
                        this.formGroup.markAsDirty();
                      },
                    },
                  },
                },
              ],
            },
          ],
          tabIndex: 1,
          name: '',
        },
        // ! Members Tab
        {
          label: this.translateService.instant('editor-role.label.members'),
          key: 'members',
          icon: ['fas', 'users'],
          panels: [
            {
              panelName: 'Members Panel',
              panelLabel: '',
              layoutConfig: {
                direction: 'column',
                gap: '0.75rem',
              },
              items: [
                {
                  propertyName: 'members',
                  propertyLabel: this.translateService.instant(
                    'editor-role.label.user-groups',
                  ),
                  controlType: 'component',
                  additionalConfig: {
                    component: UserGroupMembersComponent,
                    inputProvider: () =>
                      this.getMembersComponentInputs.bind(this),
                    outputs: {
                      principalsChanged: (principals: Principal[]) => {
                        this.role.members = principals;
                        this.formGroup.markAsDirty();
                      },
                    },
                  },
                },
              ],
            },
          ],
          tabIndex: 1,
          name: '',
        },
        {
          label: this.translateService.instant('editor-role.label.audit-trail'),
          key: 'audit-trail',
          icon: ['fas', 'lock'],
          availableInFormModes: [FormMode.Edit],
          panels: [
            {
              panelName: 'Audit Trail Panel',
              panelLabel: '',
              layoutConfig: {
                direction: 'column',
                gap: '0.75rem',
              },
              items: [
                {
                  propertyName: 'audit-trail',
                  propertyLabel: this.translateService.instant(
                    'editor-role.label.custom-component',
                  ),
                  controlType: 'component',
                  additionalConfig: {
                    component: AuditTrailComponent,
                    inputs: {
                      object: this.role,
                      hasOwner: false,
                    },
                    containerStyleProperties: {
                      height: 'calc(100vh - 14rem)',
                    },
                  },
                },
              ],
            },
          ],
          tabIndex: 0,
          name: 'audit-trail',
        },
      ],
    };
  }
}
